import _ from 'lodash';
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import SparkMD5 from 'spark-md5';

import { EntityType } from '@/app/shared/constants/globalConstants';
import { PAGE_TYPE } from '@/app/shared/constants/segmentConstants';
import { useAnalytics } from '@/app/shared/hooks/useAnalytics';
import { MuiButton } from '@/app/shared/ui';
import DownArrow from '@/assets/images/down-arrow.svg';
import GreenCheckmark from '@/assets/images/green-checkmark.svg';
import GreyCheckmark from '@/assets/images/grey-checkmark.svg';
import UpArrow from '@/assets/images/up-arrow.svg';
import { useAddEventLogMutation } from '@/store/api/eventsApi';
import { useAppSelector } from '@/store/hooks';
import { markFileSubmitted, selectReferences, setUploadedDocsCount } from '@/store/reducers/uploadSlice';

import { useFetchEmergencyContactsReferencesQuery } from '../uploadStips/components/References/references.services';
import AdditionalItems from './components/AdditionalItems/AdditionalItems';
import AssetDocuments from './components/AssetDocuments/AssetDocuments';
import ContactDocuments from './components/ContactDocuments/ContactDocuments';
import References from './components/References/References';
import { TrustageSection } from './components/TrustageSection';
import DocumentFormRow from './DocumentsFormRow';

import type { AdditionalItemData, AssetDocument, BaseDocument, FilesData, OcrDetail, StipDocument } from './types';
import type { Entities, StipRequestItems, StipType } from './uploadStips.services';
import type { SelectChangeEvent } from '@mui/material';

export interface DocumentsFormProps {
  contactDocuments: StipDocument[];
  coContactDocuments: StipDocument[];
  assetDocuments: AssetDocument[];
  stipRequestItems: StipRequestItems[];
  stipsTypes: StipType[];
  createStipsData: (
    contactData: FilesData,
    assetData: FilesData,
    additionalData: Record<string, AdditionalItemData>,
    additionalItemsFiles: FilesData,
    togglesWithUploads: string[],
    isSubmitDisabled?: boolean
  ) => Promise<void>;
  assets: Entities | undefined;
  filesOcrDetails: OcrDetail[];
  fileMd5Map: Record<string, { md5: string; submitted: boolean }[]>;
  setFileMd5: (stipRequestItemId: string, md5: string) => void;
  isCDX?: boolean;
}

const DocumentsForm: React.FC<DocumentsFormProps> = ({
  contactDocuments,
  coContactDocuments,
  assetDocuments,
  stipRequestItems,
  stipsTypes,
  createStipsData,
  assets,
  filesOcrDetails,
  fileMd5Map,
  setFileMd5,
  isCDX = false,
}) => {
  const dispatch = useDispatch();
  const { startInteraction, trackClick } = useAnalytics({page_type: PAGE_TYPE.ILENDING_HUB_UPLOAD});

  const [data, setData] = useState<FilesData>({});
  const [assetDocumentsData, setAssetDocumentsData] = useState<FilesData>({});
  const [additionalItemsDocuments, setAdditionalItemsDocuments] = useState<FilesData>({});
  const [additionalItemsData, setAdditionalItemsData] = useState<Record<string, AdditionalItemData>>({});

  const [additionalItemCount, setAdditionalItemCount] = useState(0);
  const [personalInfoExpanded, setPersonalInfoExpanded] = useState(true);
  const [assetInfoExpanded, setAssetInfoExpanded] = useState(false);
  const [additionalDocumentsExpanded, setAdditionalDocumentsExpanded] = useState(false);
  const [duplicateErrors, setDuplicateErrors] = useState<Record<string, string>>({});
  const [toastMessage, setToastMessage] = useState('');
  const [addEventLog] = useAddEventLogMutation();
  console.log(additionalItemCount);
  const contactId = useAppSelector((state) => state.auth.contactId) || '';
  const references = useAppSelector(selectReferences);
  const { data: originalReferenceList = {} } = useFetchEmergencyContactsReferencesQuery(contactId || '', {
    skip: !contactId,
    refetchOnMountOrArgChange: true,
  });

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 4000);
  };

  const getDocumentStatus = useCallback(
    (doc: BaseDocument) => {
      const ocrMatch = filesOcrDetails.find(
        (f) => f.stipTypeName?.trim().toLowerCase() === doc.stipTypeName?.trim().toLowerCase()
      );

      if (ocrMatch?.isValid) return 'Received';
      if (doc.validationMessages?.[0]) return 'Pending';
      const isReceived = !!doc.stipReceivedDate || !!doc.phoenixUserUploadedDate;
      if (isReceived) return 'Received';
      return 'Pending';
    },
    [filesOcrDetails]
  );

  const getPendingDocuments = useCallback(() => {
    const combined = contactDocuments.concat(coContactDocuments);
    return combined.filter((doc) => getDocumentStatus(doc) !== 'Received');
  }, [contactDocuments, coContactDocuments, getDocumentStatus]);

  const getUploadedDocuments = useCallback(() => {
    const combined = contactDocuments.concat(coContactDocuments);
    return combined.filter((doc) => getDocumentStatus(doc) === 'Received');
  }, [contactDocuments, coContactDocuments, getDocumentStatus]);

  const getPendingAssetDocuments = useCallback(
    () => assetDocuments.filter((doc) => getDocumentStatus(doc) !== 'Received'),
    [assetDocuments, getDocumentStatus]
  );

  const getUploadedAssetDocuments = useCallback(
    () => assetDocuments.filter((doc) => getDocumentStatus(doc) === 'Received'),
    [assetDocuments, getDocumentStatus]
  );

  const areAllUploaded = (pending: BaseDocument[], uploaded: BaseDocument[]) => pending.length === 0 && uploaded.length > 0;

  const combinedDocuments = contactDocuments.concat(coContactDocuments);
  const uploadedDocuments = getUploadedDocuments();
  const uploadedAssetDocuments = getUploadedAssetDocuments();
  const pendingDocuments = getPendingDocuments();
  const pendingAssetDocuments = getPendingAssetDocuments();

  const allPersonalInfoUploaded = areAllUploaded(pendingDocuments, uploadedDocuments);
  const allAssetInfoUploaded = areAllUploaded(pendingAssetDocuments, uploadedAssetDocuments);

  const computeMd5 = (file: File) =>
    new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(SparkMD5.ArrayBuffer.hash(e.target?.result as ArrayBuffer));
      reader.readAsArrayBuffer(file);
    });

  const computeTotalUploadedCount = () => {
    const personalCount = Object.keys(data).length + uploadedDocuments.length;
    const assetCount = Object.keys(assetDocumentsData).length + uploadedAssetDocuments.length;
    const additionalCount = Object.keys(additionalItemsDocuments).length + additionalItemCount;
    return personalCount + assetCount + additionalCount;
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    stateKey: string,
    stipRequestItemId?: string,
    displayDuplicateError?: boolean
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const md5 = await computeMd5(file);

    const existingEntries = stipRequestItemId ? fileMd5Map[stipRequestItemId] || [] : [];

    const isDuplicate = existingEntries.some((entry) => entry.md5 === md5 && entry.submitted);

    if (isDuplicate && displayDuplicateError && stipRequestItemId) {
      setDuplicateErrors((prev) => ({
        ...prev,
        [stipRequestItemId]: 'This file has already been uploaded for this document type.',
      }));

      showToast('This file has already been uploaded for this document type.');

      e.target.value = '';
      return;
    }

    if (stipRequestItemId) {
      setDuplicateErrors((prev) => {
        const copy = { ...prev };
        delete copy[stipRequestItemId];
        return copy;
      });

      setFileMd5(stipRequestItemId.toString(), md5);
    }

    const update = (prev: FilesData) => ({
      ...prev,
      [e.target.name]: {
        file,
        filePreview: URL.createObjectURL(file),
      },
    });

    if (stateKey === 'data') {
      setData(update);
      trackClick('Upload Document Clicked', {
        toggle_name: 'personal_information',
        uploaded_doc_name: file.name,
        uploaded_count: Object.keys(data).length + uploadedDocuments.length + 1,
        total_required: combinedDocuments.length,
      });
    }
    else if (stateKey === 'assetDocumentsData') {
      setAssetDocumentsData(update);
      trackClick('Upload Document Clicked', {
        toggle_name: 'vehicle_information',
        uploaded_doc_name: file.name,
        uploaded_count: Object.keys(assetDocumentsData).length + uploadedAssetDocuments.length + 1,
        total_required: assetDocuments.length,
      });
    }
    else {
      setAdditionalItemsDocuments(update);
      trackClick('Upload Document Clicked', {
        toggle_name: 'additional_documents',
        uploaded_doc_name: file.name,
        uploaded_count: additionalItemCount,
      });
    }
    const totalUploaded = computeTotalUploadedCount();
    dispatch(setUploadedDocsCount(totalUploaded));

    addEventLog({ event: 'File Selected', filesize: file.size });

    e.target.value = '';
  };

  const removeFile = (fileName: string, stateKey: string) => {
    const update = (prev: FilesData) => {
      const copy = { ...prev };
      delete copy[fileName];
      return copy;
    };

    if (stateKey === 'data') setData(update);
    else if (stateKey === 'assetDocumentsData') setAssetDocumentsData(update);
    else setAdditionalItemsDocuments(update);

    addEventLog({ event: 'File Cancelled' });
    const totalUploaded = computeTotalUploadedCount();
    dispatch(setUploadedDocsCount(totalUploaded));
  };

  const isSubmitDisabled = useMemo(() => {
    const hasNewPersonalDocs = Object.keys(data).length > 0;
    const hasNewAssetDocs = Object.keys(assetDocumentsData).length > 0;

    const referencesChanged = !_.isEqual(originalReferenceList, references);

    return !(hasNewPersonalDocs || hasNewAssetDocs || referencesChanged);
  }, [data, assetDocumentsData, references, originalReferenceList]);

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    const togglesWithUploads: string[] = [];

    if (Object.keys(data).length > 0) {
      togglesWithUploads.push('personal_information');
    }

    if (Object.keys(assetDocumentsData).length > 0) {
      togglesWithUploads.push('vehicle_information');
    }

    if (Object.keys(additionalItemsDocuments).length > 0) {
      togglesWithUploads.push('additional_documents');
    }

    await createStipsData(
      data,
      assetDocumentsData,
      additionalItemsData,
      additionalItemsDocuments,
      togglesWithUploads,
      isSubmitDisabled
    );
    //Mark the contact and asset files as submitted
    Object.keys(data).forEach((key) => {
      const stipId = key.replace('document_file', '');
      const contactFileEntry = data[key];
      if (!contactFileEntry || !contactFileEntry.file) return;

      const file = contactFileEntry.file;
      computeMd5(file).then((md5) => {
        dispatch(
          markFileSubmitted({
            stipRequestItemId: stipId,
            md5,
          })
        );
      });
    });

    Object.keys(assetDocumentsData).forEach((key) => {
      const stipId = key.replace('document_file', '');
      const assetFileEntry = assetDocumentsData[key];
      if (!assetFileEntry || !assetFileEntry.file) return;

      const file = assetFileEntry.file;
      computeMd5(file).then((md5) => {
        dispatch(
          markFileSubmitted({
            stipRequestItemId: stipId,
            md5,
          })
        );
      });
    });
    localStorage.removeItem('unloaded');
  };

  const handleChange = (event: SelectChangeEvent, fileName: string) => {
    const val = event.target.value;
    const field = event.target.name;

    const stipType = stipsTypes.find((stip) => stip.stipTypeId === val);

    setAdditionalItemsData((prev) => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        [field]: val,
        stipData: stipType,
        selectedStipTypeId: stipType ? stipType.entityTypeId : null,
      },
    }));
  };

  const handleAssetChange = (event: SelectChangeEvent, fileName: string) => {
    const val = event.target.value;
    const current = additionalItemsData[fileName];
    if (!current?.stipData) return;

    const entityType = current.stipData.entityTypeId;
    const entityName = entityType === EntityType.CONTACT ? 'contacts' : entityType === EntityType.ASSET ? 'assets' : 'deals';

    const asset = assets?.[entityName]?.find((a) => a.entityDisplayName === val) ?? null;

    setAdditionalItemsData((prev) => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        [event.target.name]: val,
        assetData: asset,
        coContact: asset?.isCoborrower ?? false,
      },
    }));
  };

  const addAdditionalItemCount = () => {
    setAdditionalItemCount((prev) => {
      const newCount = prev + 1;

      setAdditionalItemsData((prevData) => ({
        ...prevData,
        [`additionalItem${newCount}`]: {
          stipsType: undefined,
          selectedStipTypeId: null,
          assets: undefined,
          coContact: false,
          assetData: null,
        },
      }));

      return newCount;
    });
  };

  const renderDocumentForm = (documents: StipDocument[]) => (
    <DocumentFormRow
      data={data}
      documents={documents}
      isAsset={false}
      filesOcrDetails={filesOcrDetails}
      duplicateErrors={duplicateErrors}
      handleFile={handleFileUpload}
      removeFile={removeFile}
    />
  );

  const renderAssetDocumentForm = (documents: AssetDocument[]) => (
    <DocumentFormRow
      data={assetDocumentsData}
      documents={documents}
      isAsset={true}
      filesOcrDetails={filesOcrDetails}
      duplicateErrors={duplicateErrors}
      handleFile={handleFileUpload}
      removeFile={removeFile}
    />
  );

  const renderAdditionalDocuments = () =>
    Object.keys(additionalItemsData).map((doc) => (
      <AdditionalItems
        key={doc}
        fileName={doc}
        data={additionalItemsDocuments}
        assetDocuments={assetDocuments}
        assets={assets}
        stipRequestItems={stipRequestItems}
        selectedStipTypeId={additionalItemsData[doc].selectedStipTypeId}
        stipsTypes={stipsTypes}
        stipsLocalState={additionalItemsData[doc]}
        assetsLocalState={additionalItemsData[doc].assets}
        handleFile={handleFileUpload}
        removeFile={removeFile}
        handleChange={handleChange}
        handleAssetChange={handleAssetChange}
      />
    ));

  return (
    <form
      onSubmit={handleSubmit}
      className="document-form__container"
    >
      {toastMessage && (
        <div className="documentsToast">
          <span className="documentsToast__message">{toastMessage}</span>
          <span
            className="documentsToast__close"
            onClick={() => setToastMessage('')}
          >
            ×
          </span>
        </div>
      )}

      <div className="document-form__upload-documents--header">
        Upload Documents
        <p className="document-form__upload-documents--description">We just need to collect a few documents from you</p>
      </div>

      <div className="document-form__card">
        <div
          className="document-form__card--header"
          onClick={() => {
            setPersonalInfoExpanded(!personalInfoExpanded);
            startInteraction('toggle_closed');
            trackClick(!personalInfoExpanded ? 'Toggle Opened' : 'Toggle Closed', {
              toggle_name: 'personal_information',
              uploaded_count: Object.keys(data).length + uploadedDocuments.length,
              total_required: combinedDocuments.length,
            });
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              className="document-form__card--checkmark"
              src={allPersonalInfoUploaded ? GreenCheckmark : GreyCheckmark}
            />
            <span className="document-form__card--header-description">
              <p className="document-form__card--header-title">Personal Information</p>
              <p className="document-form__card--header-progress">
                {Object.keys(data).length + uploadedDocuments.length}/{combinedDocuments.length} added
              </p>
            </span>
          </div>
          <img src={personalInfoExpanded ? UpArrow : DownArrow} />
        </div>

        {personalInfoExpanded && (
          <div className="document-form__card--body">
            <ContactDocuments
              documents={contactDocuments}
              renderDocumentForm={renderDocumentForm}
            />
            <ContactDocuments
              documents={coContactDocuments}
              renderDocumentForm={renderDocumentForm}
            />
          </div>
        )}
      </div>

      <div className="document-form__card">
        <div
          className="document-form__card--header"
          onClick={() => {
            setAssetInfoExpanded(!assetInfoExpanded);
            startInteraction('toggle_opened');
            trackClick(!assetInfoExpanded ? 'Toggle Opened' : 'Toggle Closed', {
              toggle_name: 'vehicle_information',
              uploaded_count: Object.keys(assetDocumentsData).length + uploadedAssetDocuments.length,
              total_required: assetDocuments.length,
            });
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              className="document-form__card--checkmark"
              src={allAssetInfoUploaded ? GreenCheckmark : GreyCheckmark}
            />
            <span className="document-form__card--header-description">
              <p className="document-form__card--header-title">Vehicle Information</p>
              <p className="document-form__card--header-progress">
                {Object.keys(assetDocumentsData).length + uploadedAssetDocuments.length}/{assetDocuments.length} added
              </p>
            </span>
          </div>
          <img src={assetInfoExpanded ? UpArrow : DownArrow} />
        </div>

        {assetInfoExpanded && (
          <div className="document-form__card--body">
            {
              <AssetDocuments
                documents={assetDocuments}
                renderDocumentForm={renderAssetDocumentForm}
              />
            }
          </div>
        )}
      </div>

      <div className="document-form__card">
        <References 
          startInteraction={startInteraction}
          trackClick={trackClick}
        />
      </div>

      <div className="document-form__card">
        <div
          className="document-form__card--header"
          onClick={() => { 
            setAdditionalDocumentsExpanded(!additionalDocumentsExpanded);
            startInteraction('toggle_opened');
            trackClick(!additionalDocumentsExpanded ? 'Toggle Opened' : 'Toggle Closed', {
              toggle_name: 'additional_documents',
              uploaded_count: additionalItemCount,
            });
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className="document-form__card--header-additionalDocuments">Additional Documents</span>
          </div>
          <img src={additionalDocumentsExpanded ? UpArrow : DownArrow} />
        </div>

        {additionalDocumentsExpanded && (
          <div className="document-form__card--body">
            {renderAdditionalDocuments()}
            <div className="document-form__button-additional">
              <MuiButton
                variant="contained"
                color="secondary"
                onClick={addAdditionalItemCount}
              >
                Add additional document
              </MuiButton>
            </div>
          </div>
        )}
      </div>

      {!isCDX && <TrustageSection contactId={contactId}/>
      }
      <div className="document-form__button-container">
        <MuiButton
          variant="contained"
          color="primary"
          disabled={isSubmitDisabled}
          fullWidth
          type="submit"
        >
          Submit
        </MuiButton>
      </div>
    </form>
  );
};

export default DocumentsForm;
